import {
  AsyncFieldError,
  AsyncValidationError,
  BookmarkablePage,
  FormFieldErrors,
  FormValues,
  getValue,
  InputWizardItemMessage,
  InputWizardPanel,
  InputWizardPanelComponent,
  MessageType,
  ReviewItem,
  ReviewSection,
  SelectOption,
  useNavigation,
} from "o4a-react";
import * as React from "react";
import { Stack } from "@fluentui/react";
import { useBoolean } from "@fluentui/react-hooks";
import * as Messages from "../../codegen/Messages";
import { DeploymentAsyncValidationPhase } from "../../components/DeploymentPhases/DeploymentAsyncValidationPhase";
import { DeployedService, DeploymentCreatePhase } from "../../components/DeploymentPhases/DeploymentCreationPhase";
import { DownloadSshKeyModal } from "../../components/SshKeyPair/DownloadSshKeyModal";
import {
  defaultName,
  DerivedFields as NestedSshInputFields,
  SshPublicKeySource,
} from "../../components/SshKeyPair/SshKeyPairInput";
import { ConsoleContext } from "../../console/ConsoleContext";
import { Settings, SettingsContext } from "../../console/SettingsContext";
import {
  PageId,
  PageRegistrationConfig,
  VM_DATABASE_RESOURCE_TYPE_PATH,
  VMDB_CREATE_ROUTE,
} from "../../constants/pluginConstants";
import {
  CreateMultiCloudVirtualNetworkDeploymentDetails,
  CreateNetworkLinkDeploymentDetails,
  GetMultiCloudVirtualNetworkDeploymentDetails,
  MultiCloudVirtualNetworkDeploymentDetails,
  MultiCloudVirtualNetworkSummary,
  RegisterExistingOciNetworkDeploymentDetails,
  VmdbDbSystemDeploymentDetails,
} from "../../gen/clients/mchub-azure-api-client";
import {
  AzureVirtualNetworkSummary,
  CreateVmdbDatabaseDetails,
  CreateVmdbDatabaseFromBackupDetails,
  CreateVmdbDbHomeDetails,
  CreateVmdbDbHomeFromBackupDetails,
  VmdbDbBackupConfig,
} from "../../gen/clients/mchub-azure-api-client-vmdb";
import { saveBlob } from "../../helpers/fileHelper";
import { parseId } from "../../helpers/idHelper";
import {
  DeploymentKinds,
  FreeFormTags,
  FutureMcvcn,
  FutureMcvcnExisting,
  FutureMcvcnNew,
  getAddressRange,
  getResourceTypeMessage,
  getTagsPerResourceTypeMap,
  ResourceType,
  VmdbCreateFlowType,
} from "../../helpers/resourceHelper";
import { getTimestamp } from "../../helpers/timeHelper";
import { validateIpAddressWithinRanges } from "../../helpers/validationHelper";
import { NavigationAnalyticsData, useAnalytics } from "../../hooks/useAnalytics";
import { useFeatures } from "../../hooks/useFeatures";
import {
  AsyncNotificationMethodKey,
  AsyncNotificationPolledResponseKey,
  VMDB_DEPLOYMENT_CREATE_POLL_DELAY,
  VMDB_DEPLOYMENT_CREATE_POLL_INTERVAL,
} from "../../models/AsyncNotificationProviders";
import { ReviewTabContent } from "../CreateCommon/ReviewTabContent";
import { Fields as TagsFields, GROUP_TAGS, TagsTabContent } from "../CreateCommon/TagsTabContent";
import { BasicsTabComponent, BasicsTabContent, Fields as BasicsFields, GROUP_BASICS } from "./BasicsTabContent";
import { BackupInfo, ConfigTabContent, Fields as ConfigFields, GROUP_CONFIG } from "./ConfigTabContent";
import { Fields as ManagementFields, GROUP_MANAGEMENT, ManagementTabContent } from "./ManagementTabContent";
import { Fields as NetworkFields, GROUP_NETWORK, NetworkTabComponent, NetworkTabContent } from "./NetworkTabContent";
import { Fields as SecurityFields, GROUP_SECURITY, SecurityTabContent } from "./SecurityTabContent";

const registrationIds = PageRegistrationConfig[PageId.VMDB_CREATE].map(config => config.key);

const onProcessAsyncValidationErrors = (
  asyncErrors: AsyncValidationError[],
): AsyncValidationError[] => asyncErrors.map(asyncError => {
  if (typeof asyncError === "string") {
    return asyncError;
  }
  const asyncFieldError = asyncError as AsyncFieldError;
  let fieldLabel;
  if (asyncFieldError.field.startsWith("multiCloudVirtualNetworkDetails.")) {
    if (asyncFieldError.field === "multiCloudVirtualNetworkDetails.name") {
      fieldLabel = Messages.labels.name();
    } else if (asyncFieldError.field === "multiCloudVirtualNetworkDetails.azureAttachedNetworkIds") {
      fieldLabel = Messages.labels.virtualNetwork();
    } else if (asyncFieldError.field === "multiCloudVirtualNetworkDetails.ociVcnCidrBlocks") {
      fieldLabel = Messages.labels.cidrBlocks();
    } else if (asyncFieldError.field === "multiCloudVirtualNetworkDetails.ociVcnOcid") {
      fieldLabel = Messages.labels.vcnOcid();
    } else if (asyncFieldError.field === "multiCloudVirtualNetworkDetails.ociSubnetOcids") {
      fieldLabel = Messages.labels.subnetOcids();
    } else if (asyncFieldError.field === "multiCloudVirtualNetworkDetails.customerNvaIpAddress") {
      fieldLabel = Messages.labels.networkVirtualAppliance();
    } else {
      return asyncFieldError;
    }

    asyncFieldError.field = NetworkFields.Mcvcn;
    asyncFieldError.error = `<strong>(${Messages.common.new()} | ${fieldLabel})</strong> ${asyncFieldError.error}`;
  } else if (asyncFieldError.field === "vmdbDbSystemDeploymentDetails.cpuCoreCount") {
    fieldLabel = Messages.labels.ocpuCountPerVM();
    asyncFieldError.field = ConfigFields.SystemModel;
    asyncFieldError.error = `<strong>(${fieldLabel})</strong> ${asyncFieldError.error}`;
  } else if (asyncFieldError.field === ConfigFields.SystemModel) {
    fieldLabel = Messages.labels.vmShape();
    asyncFieldError.field = ConfigFields.SystemModel;
    asyncFieldError.error = `<strong>(${fieldLabel})</strong> ${asyncFieldError.error}`;
  } else if (asyncFieldError.field === "vmdbDbSystemDeploymentDetails.dbHome.database.backupTDEPassword") {
    fieldLabel = Messages.labels.tdePassword();
    asyncFieldError.field = SecurityFields.TdePassword;
  }
  return asyncFieldError;
});

enum ActionIds {
  DownloadSshKey = "download-ssh-key",
}

enum CreationPhase {
  ASYNC_VALIDATE = "ASYNC_VALIDATE",
  CREATE_SUBMIT = "CREATE_SUBMIT",
}
export interface VmdbCreatePageProps {
  createFlowType?: VmdbCreateFlowType;
}

export const VmDbCreatePage = (
  { createFlowType: createType = VmdbCreateFlowType.VMDB_DATABASE }: VmdbCreatePageProps,
): JSX.Element => {
  const { enableMcvcn, enableVmdbMcvcnCreate, enableVmdbPreflight } = useFeatures();
  const [createFlowType, setCreateFlowType] = React.useState<VmdbCreateFlowType>(createType);

  const { trackActionClick, trackActionDiscard } = useAnalytics();

  const { back, navigateToSelf, customData } = useNavigation(ConsoleContext);
  const { preferredLocationPerSubscription, preferredSubscription } = React.useContext<Settings>(SettingsContext);

  const preferredLocation = preferredLocationPerSubscription[preferredSubscription] || "";

  const [subscriptionId, setSubscriptionId] = React.useState<string>("");
  const [resourceGroupName, setResourceGroupName] = React.useState<string>("");
  const [backup, setBackup] = React.useState<BackupInfo>();
  const [location, setLocation] = React.useState<string>("");

  const [configDisabledFields, setConfigDisabledFields] = React.useState<ConfigFields[] | undefined>(undefined);

  const resourceNameRef = React.useRef<string>("");
  const deploymentNameRef = React.useRef<string>("");// to hold the name to be used in failure message

  const [
    displayDownloadSshKey,
    { setFalse: hideDownloadSshKey, setTrue: showDownloadSshKey },
  ] = useBoolean(false);

  const [creationPhase, setCreationPhase] = React.useState<CreationPhase | undefined>(undefined);
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const [createVmdbPayload, setCreateVmdbPayload] = React.useState<any>();
  const asyncValidationCallbacks = React.useRef<{
    resolve:(errors?: AsyncValidationError[]) => void,
    reject: (errorMessage: string) => void,
      // eslint-disable-next-line @typescript-eslint/no-empty-function
      }>({ resolve: () => {}, reject: () => {} });

  const submitCallbacks = React.useRef<{
    reject:(errorMessage: string, validationErrors?: AsyncValidationError[]) => void
      // eslint-disable-next-line @typescript-eslint/no-empty-function
      }>({ reject: () => {} });

  React.useEffect(() => {
    if (customData?.location || customData?.backup) {
      const configFieldsToDisable = [];
      if (customData?.location) {
        setLocation(customData.location);
      }
      if (customData?.backup) {
        configFieldsToDisable.push(ConfigFields.BackupName);
        setBackup(customData.backup);
        setCreateFlowType(VmdbCreateFlowType.VMDB_DATABASE_FROM_BACKUP);
      }
      if (configFieldsToDisable.length > 0) {
        setConfigDisabledFields(configFieldsToDisable);
      }
    } else {
      setLocation(preferredLocation);
      setSubscriptionId(preferredSubscription);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [preferredLocation, preferredSubscription]);

  React.useEffect(() => {
    // In case page was navigated to directly by entring its URL in the browser
    navigateToSelf(VMDB_CREATE_ROUTE, registrationIds[0]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const [panelRef, setPanelRef] = React.useState<InputWizardPanelComponent>({} as InputWizardPanelComponent);

  const basicsTabRef = React.useRef<BasicsTabComponent>();
  const setBasicsTabRef = (basicTab: BasicsTabComponent): void => {
    basicsTabRef.current = basicTab;
  };
  const networkTabRef = React.useRef<NetworkTabComponent>();
  const setNetworkTabRef = (networkTab: NetworkTabComponent): void => {
    networkTabRef.current = networkTab;
  };

  const onClose = (): void => back();

  const onAsyncValidate = (
    formValues: FormValues,
    resolve: (errors?: AsyncValidationError[]) => void,
    reject: (errorMessage: string) => void,
  ): void => {
    setCreateVmdbPayload(buildCreateVmdbPayload(formValues));
    asyncValidationCallbacks.current = { resolve, reject };
    setCreationPhase(CreationPhase.ASYNC_VALIDATE);
  };

  const onSubmit = (formValues: FormValues): void => {
    setCreateVmdbPayload(buildCreateVmdbPayload(formValues));
    setCreationPhase(CreationPhase.CREATE_SUBMIT);
  };

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const buildCreateVmdbPayload = (formValues: FormValues): any => {
    const tags = getValue(formValues, TagsFields.tags, GROUP_TAGS);
    const tagsMap = getTagsPerResourceTypeMap(tags);

    const name = getValue(formValues, BasicsFields.Name, GROUP_BASICS);
    resourceNameRef.current = name;

    const nodeCount = Number.parseInt(getValue(formValues, ConfigFields.NodeCount, GROUP_CONFIG).text, 10);
    let cpuCoreCount = Number.parseInt(
      getValue(formValues, ConfigFields.SystemModel, GROUP_CONFIG)?.value?.cpuCoreCount,
      10,
    );
    cpuCoreCount *= nodeCount;

    const sshPublicKey = getValue(
      formValues,
      SecurityFields.SshKeyPair,
      GROUP_SECURITY,
    )?.[NestedSshInputFields.PublicKey];
    deploymentNameRef.current = `${VM_DATABASE_RESOURCE_TYPE_PATH}-${getTimestamp()}`;

    const payload = {
      subscriptionId: getValue(formValues, BasicsFields.Subscription, GROUP_BASICS)?.[0].id,
      resourceGroupName: getValue(formValues, BasicsFields.ResourceGroup, GROUP_BASICS)?.[0].text,
      createDeploymentDetails: {
        name: deploymentNameRef.current,
        parameters: {
          kind: DeploymentKinds.Vmdb,
          networkLinkDetails: !(enableMcvcn && enableVmdbMcvcnCreate) ? buildNetworkLink(formValues) : undefined,
          multiCloudVirtualNetworkDetails: enableMcvcn && enableVmdbMcvcnCreate ? buildMcvcn(formValues) : undefined,
          vmdbDbSystemDeploymentDetails: {
            kind: createFlowType === VmdbCreateFlowType.VMDB_DATABASE_FROM_BACKUP
              ? DeploymentKinds.Restore
              : DeploymentKinds.Create,
            name,
            freeformTags: tagsMap[ResourceType.DB_SYSTEM],
            hostname: getValue(formValues, NetworkFields.HostName, GROUP_NETWORK),
            nodeCount,
            shape: getValue(formValues, ConfigFields.SystemModel, GROUP_CONFIG)?.value?.shape.name,
            cpuCoreCount,
            /* Not supported for now, always ASM
            dbSystemOptions: { storageManagement: getValue(formValues, ConfigFields.StorageType, GROUP_CONFIG)[0].id },
            */
            initialDataStorageSizeInGBs: Number.parseInt(
              getValue(formValues, ConfigFields.StorageSize, GROUP_CONFIG)[0].text,
              10,
            ),
            sshPublicKeys: [sshPublicKey],
            licenseModel: getValue(formValues, ConfigFields.LicenseType, GROUP_CONFIG)?.[0].id,
            databaseEdition: getValue(formValues, ConfigFields.SoftwareEdition, GROUP_CONFIG)?.[0].id,
            dbHome: createFlowType === VmdbCreateFlowType.VMDB_DATABASE_FROM_BACKUP
              ? buildVmDbHomeFromBackupDetails(formValues, tagsMap[ResourceType.DATABASE])
              : buildVmDbHomeDetails(formValues, tagsMap[ResourceType.DATABASE]),
          } as VmdbDbSystemDeploymentDetails,
        },
      },
    };

    return payload;
  };

  const onConfirmDownloadSshKey = (): void => {
    const privateKey = getValue(
      panelRef.getFormValues?.() ?? {},
      SecurityFields.SshKeyPair,
      GROUP_SECURITY,
    )?.[NestedSshInputFields.PrivateKey];

    if (privateKey) {
      saveBlob(privateKey, `${defaultName()}.key`);
    }

    onSubmit(panelRef.getFormValues() ?? {});
  };

  const onFormSubmit = (formValues: FormValues): void => {
    const sshKeySourceFieldName = SecurityFields.SshKeyPair + NestedSshInputFields.Source;
    const sshKeySource = getValue(formValues, sshKeySourceFieldName, GROUP_SECURITY)?.[0].id;

    // if the ssh key is set to generate
    if (SshPublicKeySource.Generate === sshKeySource) {
      /**
       * Normally we should not track action click in onSubmit but this is an exception
       * since the dialog is opened automatically by the wizard and not as a result of
       * a user action.
       */
      trackActionClick(ActionIds.DownloadSshKey, PageId.VMDB_CREATE);
      // then prompt for download of the SSH key
      showDownloadSshKey();
    } else {
      onSubmit(formValues);
    }
  };

  const buildMcvcn = (formValues: FormValues): MultiCloudVirtualNetworkDeploymentDetails => {
    const data: FutureMcvcnNew | FutureMcvcnExisting | MultiCloudVirtualNetworkSummary = getValue(
      formValues,
      NetworkFields.Mcvcn,
      GROUP_NETWORK,
    )?.[0]?.data;

    // Only exists if the user has selected an existing MCVCN
    const { id } = data as MultiCloudVirtualNetworkSummary;
    // Exists if the user is creating a new MCVCN
    const { name, azureAttachedNetworkId, customerNvaIpAddress } = data as FutureMcvcn;
    // Exists if the user is creating a new MCVCN using an existing OCI VCN
    const { ociSubnetOcids, ociVcnOcid } = data as FutureMcvcnExisting;
    // Exists if the user is creating a new MCVCN using a new OCI VCN
    const { ociVcnCidrBlocks } = data as FutureMcvcnNew;

    let mcvcnDetails: MultiCloudVirtualNetworkDeploymentDetails;
    const azureAttachedNetworkIds = [azureAttachedNetworkId];

    if (id) {
      mcvcnDetails = {
        kind: DeploymentKinds.Get,
        id,
      } as GetMultiCloudVirtualNetworkDeploymentDetails;
    } else if (ociVcnCidrBlocks) {
      mcvcnDetails = {
        kind: DeploymentKinds.Create,
        azureAttachedNetworkIds,
        name,
        ociVcnCidrBlocks,
        customerNvaIpAddress,
      } as CreateMultiCloudVirtualNetworkDeploymentDetails;
    } else if (ociVcnOcid && ociSubnetOcids) {
      mcvcnDetails = {
        kind: DeploymentKinds.Register,
        azureAttachedNetworkIds,
        name,
        ociVcnOcid,
        ociSubnetOcids,
        customerNvaIpAddress,
      } as RegisterExistingOciNetworkDeploymentDetails;
    } else throw new Error("Invalid parameters to create MCVCN");

    return mcvcnDetails;
  };

  const buildNetworkLink = (formValues: FormValues): CreateNetworkLinkDeploymentDetails => {
    const name = getValue(formValues, BasicsFields.Name, GROUP_BASICS);
    const cidrRange = getValue(formValues, NetworkFields.Cidrs, GROUP_NETWORK);
    const cidr = [cidrRange];
    const networkLinkDetails: CreateNetworkLinkDeploymentDetails = {
      kind: DeploymentKinds.Create,
      name: `${name}-networkLink`,
      ociNetworkCidrs: cidr,
      azureAttachedNetworkIds: [getValue(formValues, NetworkFields.VNET, GROUP_NETWORK)?.[0].id],
      customerNvaIpAddress: getValue(formValues, NetworkFields.NetworkVirtualAppliance, GROUP_NETWORK),
    };
    return networkLinkDetails;
  };

  // eslint-disable-next-line max-len
  const buildVmDbHomeFromBackupDetails = (formValues: FormValues, tags: FreeFormTags): CreateVmdbDbHomeFromBackupDetails => {
    const vmDbHomeDetails: CreateVmdbDbHomeFromBackupDetails = {
      name: getValue(formValues, BasicsFields.Name, GROUP_BASICS),
      database: buildDatabaseFromBackupDetails(formValues, tags),
    };
    return vmDbHomeDetails;
  };

  // eslint-disable-next-line max-len
  const buildDatabaseFromBackupDetails = (formValues: FormValues, tags: FreeFormTags): CreateVmdbDatabaseFromBackupDetails => {
    const databaseDetails: CreateVmdbDatabaseFromBackupDetails = {
      freeformTags: tags,
      backupId: backup?.id || "",
      adminPassword: getValue(formValues, SecurityFields.Password, GROUP_SECURITY),
      dbName: getValue(formValues, ConfigFields.DatabaseName, GROUP_CONFIG),
      backupTDEPassword: getValue(formValues, SecurityFields.TdePassword, GROUP_SECURITY),
    };
    return databaseDetails;
  };

  const buildVmDbHomeDetails = (formValues: FormValues, tags: FreeFormTags): CreateVmdbDbHomeDetails => {
    const vmDbHomeDetails: CreateVmdbDbHomeDetails = {
      name: getValue(formValues, BasicsFields.Name, GROUP_BASICS),
      dbVersion: getValue(formValues, ConfigFields.DatabaseVersion, GROUP_CONFIG)?.[0].id,
      database: buildDatabaseDetails(formValues, tags),
    };
    return vmDbHomeDetails;
  };

  const buildDatabaseDetails = (formValues: FormValues, tags: FreeFormTags): CreateVmdbDatabaseDetails => {
    const pdbName = getValue(formValues, ConfigFields.PdbName, GROUP_CONFIG);

    const databaseDetails: CreateVmdbDatabaseDetails = {
      freeformTags: tags,
      adminPassword: getValue(formValues, SecurityFields.Password, GROUP_SECURITY),
      dbName: getValue(formValues, ConfigFields.DatabaseName, GROUP_CONFIG),
      pdbName: pdbName || undefined,
      characterSet: getValue(formValues, ConfigFields.CharacterSet, GROUP_CONFIG)?.[0].id,
      ncharacterSet: getValue(formValues, ConfigFields.NationalCharacterSet, GROUP_CONFIG)?.[0].id,
      tdeWalletPassword: getValue(formValues, SecurityFields.UseDifferentTDEPassword, GROUP_SECURITY)
        ? getValue(formValues, SecurityFields.TdePassword, GROUP_SECURITY)
        : undefined,
      dbBackupConfig: buildBackupDetails(formValues),
    };
    return databaseDetails;
  };

  const buildBackupDetails = (formValues: FormValues): VmdbDbBackupConfig => {
    const autoBackupEnabled = getValue(formValues, ManagementFields.backups, GROUP_MANAGEMENT);
    const backupDetails: VmdbDbBackupConfig = {
      autoBackupEnabled,
      autoBackupWindow: autoBackupEnabled
        ? getValue(formValues, ManagementFields.scheduling, GROUP_MANAGEMENT)?.[0].id : undefined,
      recoveryWindowInDays: autoBackupEnabled
        ? Number(getValue(formValues, ManagementFields.retentionPeriod, GROUP_MANAGEMENT)?.[0].id) : undefined,
    };
    return backupDetails;
  };

  const buildBasicInfo = (formValues: FormValues): ReviewItem[] => {
    const reviewBasicInfo: ReviewItem[] = [
      {
        label: Messages.labels.subscription(),
        value: getValue(formValues, BasicsFields.Subscription, GROUP_BASICS)?.[0].text,
      },
      {
        label: Messages.labels.resourceGroup(),
        value: getValue(formValues, BasicsFields.ResourceGroup, GROUP_BASICS)?.[0].text,
      },
      {
        label: Messages.labels.vmDatabaseName(),
        value: getValue(formValues, BasicsFields.Name, GROUP_BASICS),
      },
      {
        label: Messages.labels.region(),
        value: getValue(formValues, BasicsFields.Region, GROUP_BASICS)?.[0].text,
      },
    ];
    return reviewBasicInfo;
  };
  const buildConfigurationInfo = (formValues: FormValues): ReviewItem[] => {
    const shapeTitle = getValue(formValues, ConfigFields.SystemModel, GROUP_CONFIG).title;
    const shapeSummary = getValue(formValues, ConfigFields.SystemModel, GROUP_CONFIG).summary;
    const reviewConfigInfo: ReviewItem[] = [
      {
        label: Messages.labels.shapeSelection(),
        value: `${shapeTitle} (${shapeSummary})`,
      },
      {
        label: Messages.labels.nodeCount(),
        value: getValue(formValues, ConfigFields.NodeCount, GROUP_CONFIG).text,
      },
      /* Not supported for now, always ASM
      {
        label: Messages.labels.storageType(),
        value: getValue(formValues, ConfigFields.StorageType, GROUP_CONFIG).text,
      },
      */
      {
        label: Messages.labels.availableDataStorageGB(),
        value: getValue(formValues, ConfigFields.StorageSize, GROUP_CONFIG)[0].text,
      },
      {
        label: Messages.labels.softwareEdition(),
        value: getValue(formValues, ConfigFields.SoftwareEdition, GROUP_CONFIG)?.[0].text,
      },
      {
        label: Messages.labels.licenseType(),
        value: getValue(formValues, ConfigFields.LicenseType, GROUP_CONFIG)?.[0].text,
      },
    ];

    if (createFlowType === VmdbCreateFlowType.VMDB_DATABASE_FROM_BACKUP) {
      reviewConfigInfo.push(
        {
          label: Messages.labels.databaseBackupName(),
          value: getValue(formValues, ConfigFields.BackupName, GROUP_CONFIG),
        },
        {
          label: Messages.labels.databaseName(),
          value: getValue(formValues, ConfigFields.DatabaseName, GROUP_CONFIG),
        },
      );
    } else {
      reviewConfigInfo.push(
        {
          label: Messages.labels.databaseVersion(),
          value: getValue(formValues, ConfigFields.DatabaseVersion, GROUP_CONFIG)?.[0].text,
        },
        {
          label: Messages.labels.databaseName(),
          value: getValue(formValues, ConfigFields.DatabaseName, GROUP_CONFIG),
        },
        {
          label: Messages.labels.pdbName(),
          value: getValue(formValues, ConfigFields.PdbName, GROUP_CONFIG),
        },
        {
          label: Messages.labels.characterSet(),
          value: getValue(formValues, ConfigFields.CharacterSet, GROUP_CONFIG)?.[0].text,
        },
        {
          label: Messages.labels.nationalCharacterSet(),
          value: getValue(formValues, ConfigFields.NationalCharacterSet, GROUP_CONFIG)?.[0].text,
        },
      );
    }

    return reviewConfigInfo;
  };

  const buildNetworkingInfo = (formValues: FormValues): ReviewItem[] => {
    const renderCidrValue = (values: string[]): JSX.Element => {
      const cidrs = values.map(range => (
        <Stack horizontal key={range}>
          <span style={{ paddingBottom: 1 }}>
            {range}
          </span>
        </Stack>
      ));
      return (
        <div>
          {cidrs}
        </div>
      );
    };

    const renderOcidValue = (values: string[]): JSX.Element => {
      const ocids = values.map(ocid => (
        <Stack horizontal key={ocid}>
          <span style={{ paddingBottom: 1 }}>
            {ocid}
          </span>
        </Stack>
      ));
      return (
        <div>
          {ocids}
        </div>
      );
    };

    const reviewNetworkingInfo = [];

    if (!(enableMcvcn && enableVmdbMcvcnCreate)) {
      const cidrRange = getValue(formValues, NetworkFields.Cidrs, GROUP_NETWORK);
      const cidrAddresses = getAddressRange(cidrRange);
      const cidr = [`${cidrRange} [${cidrAddresses}]`];
      const networkVirtualAppliance = getValue(formValues, NetworkFields.NetworkVirtualAppliance, GROUP_NETWORK);

      reviewNetworkingInfo.push(
        {
          label: Messages.labels.hostName(),
          value: getValue(formValues, NetworkFields.HostName, GROUP_NETWORK),
        },
        {
          label: Messages.labels.virtualNetwork(),
          // eslint-disable-next-line max-len
          value: getValue(formValues, NetworkFields.VNET, GROUP_NETWORK)?.[0].text,
        },
        ...(networkVirtualAppliance
          ? [{ label: Messages.labels.networkVirtualAppliance(), value: networkVirtualAppliance }]
          : []),
        {
          label: Messages.labels.ociCidrs(),
          value: renderCidrValue(cidr),
        },
      );
    }

    if (enableMcvcn && enableVmdbMcvcnCreate) {
      const mcvcn: MultiCloudVirtualNetworkSummary | FutureMcvcnExisting | FutureMcvcnNew | undefined = getValue(
        formValues,
        NetworkFields.Mcvcn,
        GROUP_NETWORK,
      )?.[0].data;

      if (!mcvcn) return [];

      // ID only exists if the user has selected an existing MCVCN
      const createNewMcvcn = !(mcvcn as MultiCloudVirtualNetworkSummary).id;

      // Exists if the user is creating a new MCVCN
      const { name, customerNvaIpAddress, azureAttachedNetworkId } = mcvcn as FutureMcvcn;
      const virtualNetwork = decodeURIComponent(parseId(azureAttachedNetworkId)?.resourceName || "");

      // Exists if the user is creating a new MCVCN using a new OCI VCN
      const { ociVcnCidrBlocks } = mcvcn as FutureMcvcnNew;
      // Exists if the user is creating a new MCVCN using an existing OCI VCN
      const { ociSubnetOcids, ociVcnOcid } = mcvcn as FutureMcvcnExisting;

      reviewNetworkingInfo.push(
        {
          label: Messages.labels.hostName(),
          value: getValue(formValues, NetworkFields.HostName, GROUP_NETWORK),
        },
        {
          label: Messages.labels.virtualCloudNetwork(),
          value: name,
        },
      );

      if (createNewMcvcn) {
        reviewNetworkingInfo.push(
          ...(virtualNetwork
            ? [{ label: Messages.labels.virtualNetwork(), value: virtualNetwork }]
            : []),
          ...(customerNvaIpAddress
            ? [{ label: Messages.labels.networkVirtualAppliance(), value: customerNvaIpAddress }]
            : []),
          ...(ociVcnOcid
            ? [{ label: Messages.labels.vcnOcid(), value: ociVcnOcid }]
            : []),
          ...(ociSubnetOcids
            ? [{ label: Messages.labels.subnetOcids(), value: renderOcidValue(ociSubnetOcids) }]
            : []),
          ...(ociVcnCidrBlocks
            ? [{
              label: Messages.labels.cidrBlocks(),
              value: renderCidrValue(ociVcnCidrBlocks.map(cidr => `${cidr} [${getAddressRange(cidr)}]`)),
            }]
            : []),
        );
      }
    }

    return reviewNetworkingInfo;
  };

  const buildSecurityInfo = (formValues: FormValues): ReviewItem[] => {
    const sshKeySourceFieldName = SecurityFields.SshKeyPair + NestedSshInputFields.Source;
    const sshKeySource = getValue(formValues, sshKeySourceFieldName, GROUP_SECURITY)?.[0].id;

    const sshKeyValue = sshKeySource !== undefined ? sshKeySource === SshPublicKeySource.Upload
      ? Messages.common.uploaded() : Messages.common.generated() : Messages.common.no();
    const reviewSecurityInfo: ReviewItem[] = [
      {
        label: Messages.labels.sshPublicKey(),
        value: sshKeyValue,
      },
      {
        label: Messages.labels.username(),
        value: getValue(formValues, SecurityFields.Username, GROUP_SECURITY),
      },
    ];
    if (createFlowType !== VmdbCreateFlowType.VMDB_DATABASE_FROM_BACKUP) {
      reviewSecurityInfo.push(
        {
          label: Messages.labels.useDifferentTDEPassword(),
          value: getValue(formValues, SecurityFields.UseDifferentTDEPassword, GROUP_SECURITY).toString(),
        },
      );
    }
    return reviewSecurityInfo;
  };
  const buildManagementInfo = (formValues: FormValues): ReviewItem[] => {
    const autoBackupEnabled = getValue(formValues, ManagementFields.backups, GROUP_MANAGEMENT);
    const reviewManagementInfo: ReviewItem[] = [
      {
        label: Messages.labels.enableAutomaticBackups(),
        value: autoBackupEnabled.toString(),
      },
    ];
    if (autoBackupEnabled) {
      reviewManagementInfo.push(
        {
          label: Messages.labels.backupRetentionPeriod(),
          value: getValue(formValues, ManagementFields.retentionPeriod, GROUP_MANAGEMENT)?.[0].text,
        },
        {
          label: `${Messages.labels.backupScheduling()} (UTC)`,
          value: getValue(formValues, ManagementFields.scheduling, GROUP_MANAGEMENT)?.[0].text,
        },
      );
    }
    return reviewManagementInfo;
  };

  const buildTagsInfo = (formValues: FormValues): ReviewItem[] => {
    const tagsInfo = getValue(formValues, TagsFields.tags, GROUP_TAGS);
    const tags: { label: string; value: string; }[] = [];
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    tagsInfo.forEach((element: any) => (
      element.resources.forEach((resource: { [key: string]: string }) => (
        element.name !== undefined && tags.push({
          label: element.name,
          value: `${element.value !== undefined ? element.value : ""}(${resource.text})`,
        })
      ))));
    if (tags.length === 0) tags.push({ label: "None", value: "" });
    tags.sort((a, b) => ((a.value.split("(")[1].split(")")[0] > b.value.split("(")[1].split(")")[0]) ? 1 : -1));
    return tags;
  };

  const onRenderReview = (formValues: FormValues): JSX.Element => {
    const reviewSections = [
      { title: Messages.createExaDb.basicsTab.title(), items: buildBasicInfo(formValues) },
      { title: Messages.createExaDb.configurationTab.title(), items: buildConfigurationInfo(formValues) },
      { title: Messages.createExaDb.networkingTab.title(), items: buildNetworkingInfo(formValues) },
      { title: Messages.createExaDb.securityTab.title(), items: buildSecurityInfo(formValues) },
    ] as ReviewSection[];
    if (createFlowType !== VmdbCreateFlowType.VMDB_DATABASE_FROM_BACKUP) {
      reviewSections.push(
        { title: Messages.createExaDb.managementTab.title(), items: buildManagementInfo(formValues) },
      );
    }
    reviewSections.push({ title: Messages.tags.title(), items: buildTagsInfo(formValues) });
    return <ReviewTabContent reviewSections={reviewSections} />;
  };

  const getTagsResourceTypeOptions = (): SelectOption[] => {
    const resourceOptions: SelectOption[] = [
      { id: ResourceType.DATABASE, text: getResourceTypeMessage(ResourceType.DATABASE) },
      { id: ResourceType.DB_SYSTEM, text: getResourceTypeMessage(ResourceType.DB_SYSTEM) },
    ];
    return resourceOptions;
  };

  const wizardItems = [
    {
      header: Messages.createVmDb.basicsTab.title(),
      groupName: GROUP_BASICS,
      content: (
        <BasicsTabContent
          inputWizardPanelRef={panelRef}
          componentRef={setBasicsTabRef}
          location={location}
          subscription={subscriptionId}
          resourceGroupId={undefined}
          disableUIControls={undefined}
        />
      ),
      onUnselecting: () => basicsTabRef.current?.closeSidePanels(),
    },
    {
      header: Messages.createVmDb.configurationTab.title(),
      groupName: GROUP_CONFIG,
      content: (
        <ConfigTabContent
          createFlowType={createFlowType}
          location={location}
          inputWizardPanelRef={panelRef}
          subscriptionId={subscriptionId}
          backup={backup}
          disableUIControls={configDisabledFields}
        />
      ),
    },
    {
      header: Messages.createVmDb.networkingTab.title(),
      groupName: GROUP_NETWORK,
      content: (
        <NetworkTabContent
          componentRef={setNetworkTabRef}
          inputWizardPanelRef={panelRef}
          subscriptionId={subscriptionId}
          location={location}
        />
      ),
      onUnselecting: () => networkTabRef.current?.closeSidePanels(),
    },
    {
      header: Messages.createVmDb.securityTab.title(),
      groupName: GROUP_SECURITY,
      content: <SecurityTabContent createFlowType={createFlowType} />,
    },
  ];
  if (createFlowType !== VmdbCreateFlowType.VMDB_DATABASE_FROM_BACKUP) {
    wizardItems.push(
      {
        header: Messages.createVmDb.managementTab.title(),
        groupName: GROUP_MANAGEMENT,
        content: <ManagementTabContent />,
      },
    );
  }
  wizardItems.push(
    {
      header: Messages.tags.title(),
      groupName: GROUP_TAGS,
      content: <TagsTabContent resourceOptions={getTagsResourceTypeOptions()} />,
    },
  );

  const formValidator = (formValues: FormValues): FormFieldErrors[] | undefined => {
    const formFieldErrors: FormFieldErrors[] = [];

    const virtualNetwork: AzureVirtualNetworkSummary = getValue(
      formValues,
      NetworkFields.VNET,
      GROUP_NETWORK,
    )?.[0]?.data;

    const networkVirtualAppliance = getValue(formValues, NetworkFields.NetworkVirtualAppliance, GROUP_NETWORK);

    formFieldErrors.push({
      errors:
        networkVirtualAppliance
          ? validateIpAddressWithinRanges(networkVirtualAppliance, virtualNetwork?.addressSpaces ?? [])
          : undefined,
      field: NetworkFields.NetworkVirtualAppliance,
      group: GROUP_NETWORK,
    });

    return formFieldErrors;
  };

  return (
    <BookmarkablePage
      appContext={ConsoleContext}
      registrationIds={registrationIds}
      title={createFlowType === VmdbCreateFlowType.VMDB_DATABASE_FROM_BACKUP
        ? Messages.createVmDb.titles.shortFromBackup()
        : Messages.createVmDb.titles.short()}
    >
      <InputWizardPanel
        componentRef={setPanelRef}
        title={createFlowType === VmdbCreateFlowType.VMDB_DATABASE_FROM_BACKUP
          ? Messages.createVmDb.titles.longFromBackup()
          : Messages.createVmDb.titles.long()}
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        onFormChange={(_formValues: FormValues, fieldValue: any, field: string) => {
          if (field === BasicsFields.Subscription) {
            setSubscriptionId(fieldValue?.[0]?.id);
          } else if (field === BasicsFields.ResourceGroup) {
            setResourceGroupName(fieldValue?.[0]?.text);
          } else if (field === BasicsFields.Region) {
            setLocation(fieldValue?.[0]?.id);
          }
        }}
        items={wizardItems}
        itemsMessages={[{
          itemNdx: 0,
          type: MessageType.WARNING,
          message: Messages.createCommon.dependencyWarnings.basicsChangeWarning(Messages.labels.vmDatabase()),
          hideOnFirstSelect: true,
        }] as InputWizardItemMessage[]}
        validator={formValidator}
        onRenderReview={onRenderReview}
        onUnselectingReview={() => {
          setCreateVmdbPayload(undefined);
          setCreationPhase(undefined);
        }}
        onAsyncValidate={enableVmdbPreflight ? onAsyncValidate : undefined}
        onSubmit={(
          formValues: FormValues,
          reject: (errorMessage: string, validationErrors?: AsyncValidationError[]) => void,
        ) => {
          submitCallbacks.current = { reject };
          onFormSubmit(formValues);
        }}
        onClose={() => {
          const analytics = customData?.analytics as NavigationAnalyticsData;
          if (analytics) {
            trackActionDiscard(analytics.actionName, analytics.pageId, analytics.panelId);
          }
          onClose();
        }}
      />
      {creationPhase === CreationPhase.CREATE_SUBMIT && (
        <DeploymentCreatePhase
          panelRef={panelRef}
          deployedService={DeployedService.VM_DATABASE}
          subscriptionId={subscriptionId}
          resourceGroupName={resourceGroupName}
          location={location}
          resourceName={resourceNameRef.current}
          deploymentName={deploymentNameRef.current}
          submitPayload={createVmdbPayload}
          submitReject={submitCallbacks.current.reject}
          onProcessAsyncValidationErrors={onProcessAsyncValidationErrors}
          asyncNotification={{
            methodKey: AsyncNotificationMethodKey.DEPLOYMENT_GET,
            delay: VMDB_DEPLOYMENT_CREATE_POLL_DELAY,
            pollingInterval: VMDB_DEPLOYMENT_CREATE_POLL_INTERVAL,
            polledResponseKey: AsyncNotificationPolledResponseKey.VMDB_DEPLOYMENT_CREATED_CHECK,
          }}
          onPostSubmit={() => {
            setCreateVmdbPayload(undefined);
            setCreationPhase(undefined);
          }}
        />
      )}
      {creationPhase === CreationPhase.ASYNC_VALIDATE && (
        <DeploymentAsyncValidationPhase
          location={location}
          asyncValidatePayload={createVmdbPayload}
          asyncValidateResolve={asyncValidationCallbacks.current.resolve}
          asyncValidateReject={asyncValidationCallbacks.current.reject}
          onProcessAsyncValidationErrors={onProcessAsyncValidationErrors}
          onPostAsyncValidate={() => {
            setCreateVmdbPayload(undefined);
            setCreationPhase(undefined);
          }}
        />
      )}
      <DownloadSshKeyModal
        closeModal={hideDownloadSshKey}
        submitBtnText={Messages.actions.downloadKeyCreateResource()}
        cancelBtnText={Messages.actions.returnToCreateResource()}
        isOpen={displayDownloadSshKey}
        onCancel={() => {
          trackActionDiscard(ActionIds.DownloadSshKey, PageId.VMDB_CREATE);
          panelRef.allowResubmit();
        }}
        onSubmit={() => {
          onConfirmDownloadSshKey();
        }}
      />
    </BookmarkablePage>
  );
};
